import '../../../../../../assets/css/autocomplete_input.css';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import marker from '../../../../../../assets/icons/ic_marker.svg';
import { useDictionary } from '../../../../../../utilities/hooks/UseDictionary';
import AutocompleteResultItem from '../../../../common/google_maps_api/AutocompleteResultItem';
import AdvancedMarker from '../../../../common/google_maps_api/AdvancedMarker';
import {
	fetchRecommendationAutocomplete,
	getRecommendationPlace
} from '../../networking';

const LIBRARIES = ['places', 'marker'];
const DEFAULT_CENTER = { lat: 38.0068661, lng: 23.6950456 };

// Step 2 of the recommendation wizard: pick the place (Google autocomplete →
// place_id) + directions. The provider-scoped autocomplete/place-detail
// endpoints resolve to a place_id, which create/update send on save; the backend
// find-or-creates the POI.
//
// Unlike the experience meeting point, the map pin is NOT draggable: recommendations
// require a real place (place_id), so the only way to move the pin is to pick another
// place from autocomplete. The map is display-only, centred on the selected place.
const RecommendationLocation = ({ place, setPlace, directions, setDirections }) => {
	const dictionary = useDictionary();

	const [inputValue, setInputValue] = useState('');
	const [suggestions, setSuggestions] = useState([]);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const [showDropdown, setShowDropdown] = useState(false);
	const [sessionToken, setSessionToken] = useState(uuidv4());

	const containerRef = useRef(null);
	const debounceTimeout = useRef(null);

	// Map state. Pin/center follow the selected place (autocomplete pick or edit-load).
	const mapRef = useRef(null);
	const [zoom, setZoom] = useState(place?.location ? 16 : 7);
	const [center, setCenter] = useState(
		place?.location
			? { lat: place.location.latitude, lng: place.location.longitude }
			: DEFAULT_CENTER
	);
	const [placeMarker, setPlaceMarker] = useState(
		place?.location
			? { lat: place.location.latitude, lng: place.location.longitude }
			: null
	);
	const [placeMarkerContent, setPlaceMarkerContent] = useState(null);

	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
		libraries: LIBRARIES,
		mapIds: [process.env.REACT_APP_GOOGLE_MAP_ID]
	});

	// Marker image
	useEffect(() => {
		const placeImg = new Image();
		placeImg.src = marker;
		setPlaceMarkerContent(placeImg);
	}, []);

	// Keep the pin + center synced to the selected place (covers both the
	// autocomplete selection and the existing place loaded on edit).
	useEffect(() => {
		if (place?.location) {
			const next = {
				lat: place.location.latitude,
				lng: place.location.longitude
			};
			setPlaceMarker(next);
			setCenter(next);
			setZoom(16);
		} else {
			setPlaceMarker(null);
		}
	}, [place?.location]);

	const onLoad = useCallback((mapInstance) => {
		mapRef.current = mapInstance;
	}, []);

	const fetchSuggestions = useCallback(
		(query) => {
			if (query.length < 3) {
				setSuggestions([]);
				setShowDropdown(false);
				return;
			}
			clearTimeout(debounceTimeout.current);
			debounceTimeout.current = setTimeout(async () => {
				try {
					const response = await fetchRecommendationAutocomplete(
						query,
						sessionToken
					);
					setSuggestions(response.data.suggestions || []);
					setShowDropdown(true);
				} catch (error) {
					setSuggestions([]);
				}
			}, 300);
		},
		[sessionToken]
	);

	const handleSelect = (placeText, placeId) => {
		setInputValue('');
		setShowDropdown(false);
		getRecommendationPlace(placeId, sessionToken)
			.then((response) => {
				const data = response.data;
				setPlace({
					placeId: data?.id || placeId,
					name: data?.displayName?.text || placeText,
					address: data?.formattedAddress || null,
					location: data?.location
						? {
								latitude: data.location.latitude,
								longitude: data.location.longitude
						  }
						: null
				});
				setSessionToken(uuidv4());
			})
			.catch(() => {
				// keep the picker open so the provider can retry
				setShowDropdown(true);
			});
	};

	const handleKeyDown = (e) => {
		if (suggestions.length === 0) return;
		if (e.key === 'ArrowDown') {
			setSelectedIndex((prev) =>
				prev < suggestions.length - 1 ? prev + 1 : 0
			);
		} else if (e.key === 'ArrowUp') {
			setSelectedIndex((prev) =>
				prev > 0 ? prev - 1 : suggestions.length - 1
			);
		} else if (e.key === 'Enter' && selectedIndex !== -1) {
			const selected = suggestions[selectedIndex];
			handleSelect(
				selected.placePrediction?.text?.text,
				selected.placePrediction?.placeId
			);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setShowDropdown(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<div className="new-experience-form-wrapper">
			<div className="new-experience-form-container thin-scrollbar-trans">
				<h2>{dictionary.recommendation_step_location}</h2>

				<div className="group-inputs">
					<div className="autocomplete-container" ref={containerRef}>
						<div className="autocomplete-input-container">
							<div className="autocomplete-address-icon" />
							<input
								type="text"
								placeholder={dictionary.autocomplete_search}
								value={inputValue}
								onChange={(e) => {
									setInputValue(e.target.value);
									fetchSuggestions(e.target.value);
								}}
								onKeyDown={handleKeyDown}
								className="autocomplete-input"
								style={{ paddingLeft: '30px' }}
								data-testid="recommendation-place-input"
							/>
						</div>
						{showDropdown && suggestions.length > 0 && (
							<div className="autocomplete-dropdown">
								{suggestions.map((suggestionItem, index) => (
									<AutocompleteResultItem
										key={index}
										suggestionItem={suggestionItem}
										onSelect={handleSelect}
										isSelected={index === selectedIndex}
									/>
								))}
							</div>
						)}
					</div>

					{place?.name ? (
						<div className="place-details">
							<h3>{place.name}</h3>
							{place.address ? <p>{place.address}</p> : null}
						</div>
					) : null}
				</div>

				{isLoaded && (
					<div className="experience-map" data-testid="recommendation-map">
						<GoogleMap
							zoom={zoom}
							center={center}
							onLoad={onLoad}
							options={{
								zoomControl: false,
								streetViewControl: false,
								mapId: process.env.REACT_APP_GOOGLE_MAP_ID
							}}
							mapContainerClassName="experience-map"
						>
							{placeMarker && (
								<AdvancedMarker
									map={mapRef.current}
									position={placeMarker}
									title={place?.name || 'Local favorite'}
									content={placeMarkerContent}
									draggable={false}
								/>
							)}
						</GoogleMap>
					</div>
				)}

				<div style={{ marginTop: '15px' }} className="text">
					{dictionary.directions}
				</div>
				<textarea
					style={{ marginBottom: '20px' }}
					value={directions ? directions : ''}
					onChange={(e) => setDirections(e.target.value)}
					placeholder={dictionary.please_provide_directions}
					data-testid="recommendation-directions-input"
				/>
			</div>
		</div>
	);
};

RecommendationLocation.propTypes = {
	place: PropTypes.object,
	setPlace: PropTypes.func.isRequired,
	directions: PropTypes.string,
	setDirections: PropTypes.func.isRequired
};

export default RecommendationLocation;
